import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  FileText, LayoutDashboard, FolderOpen, FileCheck,
  MessageCircle, CreditCard, LogOut, Menu, X,
  Users, Tag, Settings, Sparkles, BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isSuperAdmin = user?.admin_role === "super_admin";
  const userPermissions = user?.permissions || [];

  const hasPermission = (permission) => {
    if (isSuperAdmin) return true;
    return userPermissions.includes(permission) || userPermissions.includes("all");
  };

  // All nav items including Phase 2
  const allNavItems = [
    // ── Phase 1 ──────────────────────────────────────────────
    {
      path: "/admin",
      label: "Dashboard",
      icon: <LayoutDashboard size={20} />,
      permission: null,
    },
    {
      path: "/admin/plans",
      label: "Tax Plans",
      icon: <FolderOpen size={20} />,
      permission: "manage_plans",
    },
    {
      path: "/admin/requests",
      label: "Requests",
      icon: <FileText size={20} />,
      permission: "view_requests",
    },
    {
      path: "/admin/documents",
      label: "Documents",
      icon: <FileCheck size={20} />,
      permission: "review_documents",
    },
    {
      path: "/admin/messages",
      label: "Messages",
      icon: <MessageCircle size={20} />,
      permission: "send_messages",
    },
    {
      path: "/admin/payments",
      label: "Payments",
      icon: <CreditCard size={20} />,
      permission: null,
      superAdminOnly: true,
    },
    {
      path: "/admin/offers",
      label: "Offers",
      icon: <Tag size={20} />,
      permission: "manage_offers",
      superAdminOnly: true,
    },
    {
      path: "/admin/users",
      label: "Users",
      icon: <Users size={20} />,
      permission: "view_users",
    },
    {
      path: "/admin/settings",
      label: "Settings",
      icon: <Settings size={20} />,
      permission: null,
      superAdminOnly: true,
    },

    // ── Phase 2 ──────────────────────────────────────────────
    {
      path: "/admin/ai-filing",
      label: "AI Filing",
      icon: <Sparkles size={20} />,
      permission: null,
      superAdminOnly: true,
    },
  ];

  const navItems = allNavItems.filter((item) => {
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (item.permission === null) return true;
    return hasPermission(item.permission);
  });

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getRoleBadge = () => {
    if (user?.admin_role === "super_admin") {
      return <Badge className="text-xs bg-orange-500 text-white">Super Admin</Badge>;
    }
    return <Badge className="text-xs">CA Admin</Badge>;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--secondary)" }}>
      {/* Sidebar */}
      <aside
        className={`admin-sidebar h-screen flex flex-col p-6 transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2 mb-10">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "var(--accent)" }}
          >
            <img src="/mooltax-logo.png" alt="MoolTax" style={{ height: "32px", width: "auto" }} />
          </div>
          <span className="text-xl font-bold" style={{ fontFamily: "Fraunces, serif" }}>
            MoolTax
          </span>
        </div>

        <nav className="space-y-2 flex-1 overflow-y-auto min-h-0 pr-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`nav-pill flex items-center gap-3 w-full ${
                location.pathname === item.path ? "active" : ""
              }`}
              data-testid={`admin-nav-${item.path.split("/").pop() || "dashboard"}`}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>

        <div
          className="mt-6 pt-6 border-t"
          style={{ borderColor: "rgba(255,255,255,0.12)" }}
        >
          <div className="flex items-center gap-3 mb-4 px-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
            >
              <span className="font-semibold">{user?.name?.charAt(0)}</span>
            </div>
            <div>
              <p className="font-medium text-sm">{user?.name}</p>
              {getRoleBadge()}
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-white opacity-70 hover:opacity-100 hover:bg-white/10"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut size={20} />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass-header px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <img src="/mooltax-logo.png" alt="MoolTax" style={{ height: "28px", width: "auto" }} />
        <div className="w-6"></div>
      </header>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Content */}
      <main className="admin-content p-6 lg:p-8">{children}</main>
    </div>
  );
};